import { createContext, useContext, useEffect, useState } from "react";
import {
  getUltimateAgentProfile,
  saveUltimateAgentProfile,
} from "./ultimateAgentProfile";
import { INITIAL_MAINTENANCE_REQUESTS } from "../maintenanceRequestsData";
import { executeUltimateAgentCommand, getUltimateAgentApiMode, ULTIMATE_AGENT_API_MODE, ULTIMATE_AGENT_COMMANDS, useUltimateAgentQuery } from "../api";

export const UA_ROLES = [
  { key: "LANDLORD", label: "Landlord" },
  { key: "AGENT",    label: "Agent" },
  { key: "TENANT",   label: "Tenant" },
];

const UltimateAgentContext = createContext();

export const UltimateAgentProvider = ({ children }) => {
  const apiMode = getUltimateAgentApiMode();
  const mockProfile = getUltimateAgentProfile();
  const [profile, setProfile] = useState(apiMode === ULTIMATE_AGENT_API_MODE.MOCK ? mockProfile : null);
  const [switchingRole, setSwitchingRole] = useState(null);
  const [roleSwitchError, setRoleSwitchError] = useState(null);
  const [maintenanceRequests, setMaintenanceRequests] = useState(apiMode === ULTIMATE_AGENT_API_MODE.MOCK ? INITIAL_MAINTENANCE_REQUESTS : []);
  const { data: queriedProfile, error: profileError } = useUltimateAgentQuery(
    ULTIMATE_AGENT_COMMANDS.PROFILE.GET,
    {},
    mockProfile,
    { initialData: mockProfile },
  );
  useEffect(() => {
    if (queriedProfile) setProfile(saveUltimateAgentProfile(queriedProfile));
  }, [queriedProfile]);

  const addMaintenanceRequest = (request) => {
    setMaintenanceRequests((current) => current.some((item) => item.id === request.id) ? current : [request, ...current]);
  };

  const updateMaintenanceRequest = (request) => {
    setMaintenanceRequests((current) => current.map((item) => item.id === request.id ? request : item));
  };

  const setActiveRole = async (role) => {
    const requestedRole = typeof role === "string" ? role.toUpperCase() : "";
    if (!profile?.roles?.includes(requestedRole)) return false;
    if (profile.activeRole === requestedRole) return true;

    const previousProfile = profile;
    const optimisticProfile = saveUltimateAgentProfile({
      ...profile,
      activeRole: requestedRole,
    });
    setRoleSwitchError(null);
    setSwitchingRole(requestedRole);
    setProfile(optimisticProfile);

    try {
      const result = await executeUltimateAgentCommand(
        ULTIMATE_AGENT_COMMANDS.PROFILE.UPDATE_SETTINGS,
        { activeRole: requestedRole },
        { mock: optimisticProfile },
      );
      const returnedProfile = result?.data && typeof result.data === "object"
        ? result.data
        : {};
      const returnedRoles = Array.isArray(returnedProfile.roles)
        ? returnedProfile.roles.map((item) => typeof item === "string" ? item.toUpperCase() : item)
        : [];

      // The selected persona is the requested preference. Some early backend
      // stubs return a fixed demo activeRole; accepting that value makes the UI
      // appear to ignore the user's click even though the command succeeded.
      setProfile(saveUltimateAgentProfile({
        ...optimisticProfile,
        ...returnedProfile,
        roles: returnedRoles.includes(requestedRole) ? returnedRoles : optimisticProfile.roles,
        activeRole: requestedRole,
      }));
      return true;
    } catch (error) {
      setProfile(saveUltimateAgentProfile(previousProfile));
      setRoleSwitchError(error);
      return false;
    } finally {
      setSwitchingRole(null);
    }
  };

  const updateProfile = async (changes) => {
    const result = await executeUltimateAgentCommand(
      ULTIMATE_AGENT_COMMANDS.PROFILE.UPDATE_SETTINGS,
      changes,
      { mock: { ...profile, ...changes } },
    );
    setProfile(saveUltimateAgentProfile(result.data));
  };

  if (!profile && !profileError) return <div style={{ minHeight: "60vh", display: "grid", placeItems: "center", color: "#64748b" }}>Loading UltimateAgent profile...</div>;
  if (profileError && !profile) return <div style={{ margin: 24, padding: 18, border: "1px solid #fecaca", borderRadius: 12, background: "#fef2f2", color: "#991b1b" }}>UltimateAgent profile could not be loaded. Refresh to try again.</div>;

  return (
    <UltimateAgentContext.Provider value={{
      activeRole: profile?.activeRole,
      setActiveRole,
      switchingRole,
      roleSwitchError,
      availableRoles: profile?.roles || [],
      profile,
      updateProfile,
      UA_ROLES,
      maintenanceRequests,
      addMaintenanceRequest,
      updateMaintenanceRequest,
    }}>
      {children}
    </UltimateAgentContext.Provider>
  );
};

export const useUltimateAgent = () => useContext(UltimateAgentContext);
